import React, { useEffect, useMemo, useState } from 'react';
import {
  Typography, Paper, CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Alert, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Select, MenuItem, FormControl, InputLabel, Snackbar, Box, IconButton, Tooltip,
} from '@mui/material';
import { DeleteOutline as DeleteOutlineIcon, FileDownload as FileDownloadIcon } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../api';
import { useTheme } from '@mui/material/styles';
import { getUiPreferences, patchSavedFiltersKey } from '../userPreferencesApi';

function Invoices() {
  const location = useLocation();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [form, setForm] = useState({
    invoice_number: '',
    invoice_date: '',
    due_date: '',
    client_name: '',
    client_address: '',
    client_vat: '',
    client_email: '',
    client_phone: '',
    is_company: false,
    company_reg_number: '',
    job_id: '',
    description: '',
    quantity: 1,
    unit_price: 0,
    subtotal: 0,
    vat_rate: 20,
    vat_amount: 0,
    total: 0,
    paid: false,
    payment_date: '',
    payment_method: '',
    notes: '',
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [editId, setEditId] = useState(null);
  const theme = useTheme();
  const [invoiceSearch, setInvoiceSearch] = useState('');
  const [paidFilter, setPaidFilter] = useState('all');

  const userRole = (() => {
    try {
      const raw = localStorage.getItem('user');
      if (!raw) return '';
      const u = JSON.parse(raw);
      return (u?.role && typeof u.role === 'object' ? u.role.name : u?.role) || '';
    } catch {
      return '';
    }
  })();
  const canManageInvoicePayments = ['super_admin', 'admin'].includes(userRole);

  const displayInvoices = useMemo(() => {
    let list = Array.isArray(invoices) ? invoices : [];
    const q = invoiceSearch.trim().toLowerCase();
    if (q) {
      list = list.filter((inv) =>
        String(inv.invoice_number || inv.id || '')
          .toLowerCase()
          .includes(q) ||
        String(inv.client_name || '')
          .toLowerCase()
          .includes(q)
      );
    }
    if (paidFilter === 'paid') list = list.filter((i) => i.paid === true || i.paid === 1 || i.paid === '1');
    if (paidFilter === 'unpaid') list = list.filter((i) => !(i.paid === true || i.paid === 1 || i.paid === '1'));
    return list;
  }, [invoices, invoiceSearch, paidFilter]);

  useEffect(() => {
    const fetchInvoices = async () => {
      setLoading(true);
      setError('');
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_BASE_URL}invoices`, { headers: token ? { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } : { 'Accept': 'application/json' } });
        if (res.status === 401) {
          setError('Session expired. Please log in again.');
          setLoading(false);
          setTimeout(() => { window.location.href = '/login'; }, 500);
          return;
        }
        const ct = res.headers.get('content-type') || '';
        if (!ct.includes('application/json')) {
          throw new Error('Server did not return JSON');
        }
        if (!res.ok) throw new Error('Failed to load invoices');
        const data = await res.json();
        setInvoices(Array.isArray(data) ? data : (data?.data ?? []));
      } catch (err) {
        setError(err.message || 'Failed to load invoices');
      } finally {
        setLoading(false);
      }
    };
    fetchInvoices();
    const token = localStorage.getItem('token');
    fetch(`${API_BASE_URL}collections`, { headers: token ? { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } : { 'Accept': 'application/json' } })
      .then(res => res.json())
      .then((data) => {
        const rows = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
        setJobs(rows);
      });
  }, []);

  useEffect(() => {
    const inv = getUiPreferences().savedFilters?.invoices;
    if (!inv || typeof inv !== 'object') return;
    if (typeof inv.invoiceSearch === 'string') setInvoiceSearch(inv.invoiceSearch);
    if (inv.paidFilter === 'all' || inv.paidFilter === 'paid' || inv.paidFilter === 'unpaid') setPaidFilter(inv.paidFilter);
  }, []);

  useEffect(() => {
    if (loading) return undefined;
    const t = setTimeout(() => {
      patchSavedFiltersKey('invoices', { invoiceSearch, paidFilter }).catch(() => {});
    }, 800);
    return () => clearTimeout(t);
  }, [invoiceSearch, paidFilter, loading]);

  const handleExportInvoicesCsv = async () => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_BASE_URL}export/invoices`, {
        headers: { Accept: 'text/csv', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoices-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setSnackbar({ open: true, message: 'Export failed', severity: 'error' });
    }
  };

  const handleExportQuickBooks = async () => {
    const token = localStorage.getItem('token');
    const params = new URLSearchParams({ paid: paidFilter || 'all' });
    try {
      const res = await fetch(`${API_BASE_URL}export/invoices/quickbooks?${params}`, {
        headers: { Accept: 'text/csv', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `quickbooks-invoices-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setSnackbar({ open: true, message: 'QuickBooks CSV downloaded', severity: 'success' });
    } catch (e) {
      setSnackbar({ open: true, message: 'QuickBooks export failed', severity: 'error' });
    }
  };

  // Handle auto-opening modals from Dashboard state
  useEffect(() => {
    if (location.state?.action === 'create') {
      handleOpen();
      // Clear state 
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  function handleOpen() {
    setForm({
      invoice_number: '',
      invoice_date: new Date().toISOString().slice(0, 10),
      due_date: '',
      client_name: '',
      client_address: '',
      client_vat: '',
      client_email: '',
      client_phone: '',
      is_company: false,
      company_reg_number: '',
      job_id: '',
      description: '',
      quantity: 1,
      unit_price: 0,
      subtotal: 0,
      vat_rate: 20,
      vat_amount: 0,
      total: 0,
      paid: false,
      payment_date: '',
      payment_method: '',
      notes: '',
    });
    setEditId(null);
    setOpen(true);
  }
  function handleEdit(inv) {
    // API / DB may return 0/1; MUI MenuItem values use booleans — must match for Select to work
    const asBool = (v) => v === true || v === 1 || v === '1';
    setForm({
      ...inv,
      paid: asBool(inv.paid),
      is_company: asBool(inv.is_company),
    });
    setEditId(inv.id);
    setOpen(true);
  }
  function handleClose() { setOpen(false); setEditId(null); }

  function handleChange(e) {
    const { name, value, type, checked } = e.target;
    let v = type === 'checkbox' ? checked : value;
    if (name === 'quantity' || name === 'unit_price' || name === 'vat_rate') v = Number(v);
    setForm(f => {
      const updated = { ...f, [name]: v };
      // Автоматично изчисляване на суми
      updated.subtotal = (updated.quantity * updated.unit_price) || 0;
      updated.vat_amount = updated.subtotal * (updated.vat_rate / 100);
      updated.total = updated.subtotal + updated.vat_amount;
      return updated;
    });
  }

  function handleJobSelect(e) {
    const jobId = e.target.value;
    setForm(f => {
      const job = jobs.find(j => j.id === jobId);
      return {
        ...f,
        job_id: jobId,
        client_name: job?.client || '',
        client_address: job?.address || '',
        client_email: job?.email || '',
        client_phone: job?.phone || '',
        description: job ? `Waste: ${job.waste_type || ''} | Notes: ${job.notes || ''}` : '',
      };
    });
  }

  function handleSubmit(e) {
    e.preventDefault();
    const method = editId ? 'PUT' : 'POST';
    const url = editId ? `${API_BASE_URL}invoices/${editId}` : `${API_BASE_URL}invoices`;
    const token = localStorage.getItem('token');
    fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
      body: JSON.stringify(form)
    })
      .then(res => {
        if (!res.ok) throw new Error('Failed to save invoice');
        return res.json();
      })
      .then(() => {
        setSnackbar({ open: true, message: editId ? 'Invoice updated!' : 'Invoice saved!', severity: 'success' });
        setOpen(false);
        setEditId(null);
        // Рефреш на таблицата
        const token = localStorage.getItem('token');
        return fetch(`${API_BASE_URL}invoices`, { headers: token ? { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' } : { 'Accept': 'application/json' } })
          .then(res => res.json()).then(data => setInvoices(data));
      })
      .catch(err => setSnackbar({ open: true, message: err.message || 'Error saving invoice', severity: 'error' }));
  }

  async function handleCreatePaymentLink(inv) {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}invoices/${inv.id}/payment-link`, {
        method: 'POST',
        headers: { Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.message || 'Failed to create payment link');
      if (json.payment_url) {
        await navigator.clipboard.writeText(json.payment_url);
        setSnackbar({ open: true, message: 'Payment link copied to clipboard', severity: 'success' });
      }
    } catch (e) {
      setSnackbar({ open: true, message: e.message || 'Failed', severity: 'error' });
    }
  }

  async function handleDelete(inv) {
    const label = inv.invoice_number || inv.id;
    if (!window.confirm(`Delete invoice ${label}? This cannot be undone.`)) return;
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_BASE_URL}invoices/${inv.id}`, {
        method: 'DELETE',
        headers: {
          Accept: 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || 'Failed to delete invoice');
      }
      setInvoices((prev) => prev.filter((i) => i.id !== inv.id));
      setSnackbar({ open: true, message: 'Invoice deleted.', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err.message || 'Error deleting invoice', severity: 'error' });
    }
  }

  async function handleDownloadPdf(inv) {
    const token = localStorage.getItem('token');
    if (!token) {
      setSnackbar({ open: true, message: 'Session expired. Please log in again.', severity: 'error' });
      return;
    }

    try {
      const res = await fetch(`${API_BASE_URL}invoices/${inv.id}/pdf`, {
        headers: {
          Accept: 'application/pdf',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        const ct = res.headers.get('content-type') || '';
        const msg = ct.includes('application/json')
          ? (await res.json().catch(() => ({})))?.message
          : await res.text().catch(() => '');
        throw new Error(msg || `Failed to download PDF (HTTP ${res.status})`);
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      const safeNum = String(inv.invoice_number || inv.id || 'invoice').replace(/[^\w.-]+/g, '_');
      a.href = url;
      a.download = `invoice_${safeNum}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setSnackbar({ open: true, message: err.message || 'Failed to download PDF', severity: 'error' });
    }
  }

  return (
    <Box sx={{ mt: 4 }}>
      <Paper sx={{ p: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.text.primary, mb: 3, fontSize: '2rem' }}>
          Invoices
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
          <Button
            variant="contained"
            onClick={handleOpen}
            sx={{
              fontWeight: 600,
              textTransform: 'none',
              background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1d4ed8 0%, #2563eb 100%)',
              },
            }}
          >
            Add Invoice
          </Button>
          <TextField
            size="small"
            label="Search"
            placeholder="Invoice # or client"
            value={invoiceSearch}
            onChange={(e) => setInvoiceSearch(e.target.value)}
            sx={{ minWidth: 200 }}
          />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Paid</InputLabel>
            <Select
              label="Paid"
              value={paidFilter}
              onChange={(e) => setPaidFilter(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="paid">Paid</MenuItem>
              <MenuItem value="unpaid">Unpaid</MenuItem>
            </Select>
          </FormControl>
          <Button variant="outlined" startIcon={<FileDownloadIcon />} onClick={handleExportInvoicesCsv} sx={{ textTransform: 'none' }}>
            Export CSV
          </Button>
          <Button variant="outlined" startIcon={<FileDownloadIcon />} onClick={handleExportQuickBooks} sx={{ textTransform: 'none' }}>
            Export for QuickBooks
          </Button>
        </Box>
        {loading && <CircularProgress />}
        {error && <Alert severity="error">{error}</Alert>}
        {!loading && !error && (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Total</TableCell>
                  <TableCell>Paid</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayInvoices.map((inv) => (
                  <TableRow key={inv.id}>
                    <TableCell>{inv.invoice_number || inv.id}</TableCell>
                    <TableCell>{inv.client_name}</TableCell>
                    <TableCell>{inv.is_company ? 'Company' : 'Private'}</TableCell>
                    <TableCell>{inv.total} GBP</TableCell>
                    <TableCell>{inv.paid ? 'Yes' : 'No'}</TableCell>
                    <TableCell>{inv.status || (inv.paid ? 'Paid' : 'Unpaid')}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          sx={{ fontWeight: 500, textTransform: 'none' }}
                          onClick={() => handleDownloadPdf(inv)}
                        >
                          Download PDF
                        </Button>
                        {!inv.paid && canManageInvoicePayments ? (
                          <Button
                            variant="outlined"
                            size="small"
                            color="secondary"
                            sx={{ fontWeight: 600, textTransform: 'none' }}
                            onClick={() => handleCreatePaymentLink(inv)}
                          >
                            Payment link
                          </Button>
                        ) : null}
                        <Button
                          variant="contained"
                          size="small"
                          sx={{ fontWeight: 600, textTransform: 'none' }}
                          onClick={() => handleEdit(inv)}
                        >
                          Edit
                        </Button>
                        <Tooltip title="Delete invoice">
                          <IconButton
                            size="small"
                            color="error"
                            aria-label="Delete invoice"
                            onClick={() => handleDelete(inv)}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
          <DialogTitle sx={{ fontWeight: 700, color: theme.palette.text.primary, fontSize: '1.5rem' }}>
            {editId ? 'Edit Invoice' : 'Add Invoice'}
          </DialogTitle>
          <form onSubmit={handleSubmit}>
            <DialogContent sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <TextField name="invoice_number" label="Invoice Number" value={form.invoice_number} onChange={handleChange} required sx={{ flex: '1 1 180px' }} />
              <TextField name="invoice_date" label="Invoice Date" type="date" value={form.invoice_date} onChange={handleChange} required InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 180px' }} />
              <TextField name="due_date" label="Due Date" type="date" value={form.due_date} onChange={handleChange} InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 180px' }} />
              <FormControl sx={{ flex: '1 1 220px' }}>
                <InputLabel>Job</InputLabel>
                <Select name="job_id" value={form.job_id} label="Job" onChange={handleJobSelect}>
                  <MenuItem value="">None</MenuItem>
                  {jobs.map(job => (
                    <MenuItem key={job.id} value={job.id}>{job.id} - {job.client} ({job.scheduled_date})</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField name="client_name" label="Client Name" value={form.client_name} onChange={handleChange} required sx={{ flex: '1 1 220px' }} />
              <TextField name="client_address" label="Client Address" value={form.client_address} onChange={handleChange} sx={{ flex: '1 1 220px' }} />
              <TextField name="client_vat" label="Client VAT" value={form.client_vat} onChange={handleChange} sx={{ flex: '1 1 120px' }} />
              <TextField name="client_email" label="Client Email" value={form.client_email} onChange={handleChange} sx={{ flex: '1 1 180px' }} />
              <TextField
                name="client_phone"
                label="Client Phone"
                value={form.client_phone}
                onChange={handleChange}
                sx={{ flex: '1 1 140px' }}
                placeholder="+44 7700 900123"
              />
              <FormControl sx={{ flex: '1 1 120px' }}>
                <InputLabel>Type</InputLabel>
                <Select name="is_company" value={form.is_company} label="Type" onChange={e => handleChange({ target: { name: 'is_company', value: e.target.value } })}>
                  <MenuItem value={false}>Private</MenuItem>
                  <MenuItem value={true}>Company</MenuItem>
                </Select>
              </FormControl>
              {form.is_company && (
                <TextField name="company_reg_number" label="Company Reg. Number" value={form.company_reg_number} onChange={handleChange} sx={{ flex: '1 1 180px' }} />
              )}
              <TextField name="description" label="Description" value={form.description} onChange={handleChange} multiline minRows={2} sx={{ flex: '1 1 320px' }} />
              <TextField name="quantity" label="Quantity" type="number" value={form.quantity} onChange={handleChange} sx={{ flex: '1 1 80px' }} />
              <TextField name="unit_price" label="Unit Price" type="number" value={form.unit_price} onChange={handleChange} sx={{ flex: '1 1 120px' }} />
              <TextField name="subtotal" label="Subtotal" type="number" value={form.subtotal} InputProps={{ readOnly: true }} sx={{ flex: '1 1 120px' }} />
              <TextField name="vat_rate" label="VAT Rate (%)" type="number" value={form.vat_rate} onChange={handleChange} sx={{ flex: '1 1 100px' }} />
              <TextField name="vat_amount" label="VAT Amount" type="number" value={form.vat_amount} InputProps={{ readOnly: true }} sx={{ flex: '1 1 120px' }} />
              <TextField name="total" label="Total" type="number" value={form.total} InputProps={{ readOnly: true }} sx={{ flex: '1 1 120px' }} />
              <FormControl sx={{ flex: '1 1 120px' }}>
                <InputLabel>Paid</InputLabel>
                <Select name="paid" value={form.paid} label="Paid" onChange={e => handleChange({ target: { name: 'paid', value: e.target.value } })}>
                  <MenuItem value={false}>No</MenuItem>
                  <MenuItem value={true}>Yes</MenuItem>
                </Select>
              </FormControl>
              {form.paid && (
                <TextField name="payment_date" label="Payment Date" type="date" value={form.payment_date} onChange={handleChange} InputLabelProps={{ shrink: true }} sx={{ flex: '1 1 180px' }} />
              )}
              <TextField name="payment_method" label="Payment Method" value={form.payment_method} onChange={handleChange} sx={{ flex: '1 1 180px' }} />
              <TextField name="notes" label="Notes" value={form.notes} onChange={handleChange} multiline minRows={2} sx={{ flex: '1 1 320px' }} />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose} sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
                Cancel
              </Button>
              <Button type="submit" variant="contained" sx={{ fontWeight: 600 }}>
                Save
              </Button>
            </DialogActions>
          </form>
        </Dialog>
        <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          <Alert severity={snackbar.severity} sx={{ width: '100%' }}>{snackbar.message}</Alert>
        </Snackbar>
      </Paper>
    </Box>
  );
}

export default Invoices; 